import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import jwt from 'jsonwebtoken';
import CryptoJS from 'crypto-js';

export const notifySuccess = (message: any) =>
  toast(<p style={{ fontSize: 16 }}>{message}</p>, {
    position: 'top-right',
    autoClose: 5000,
    hideProgressBar: false,
    // @ts-ignore
    newestOnTop: false,
    closeOnClick: true,
    rtl: false,
    pauseOnFocusLoss: true,
    draggable: true,
    pauseOnHover: true,
    type: 'success'
  });

export const notifyError = (message: any) =>
  toast(<p style={{ fontSize: 16 }}>{message}</p>, {
    position: 'top-right',
    autoClose: 5000,
    hideProgressBar: false,
    // @ts-ignore
    newestOnTop: false,
    closeOnClick: true,
    rtl: false,
    pauseOnFocusLoss: true,
    draggable: true,
    pauseOnHover: true,
    type: 'error'
  });

export const createObject = (data: FormData): Record<string, string> => {
  const object: Record<string, string> = {};

  if (!Array.prototype.forEach) {
    Array.prototype.forEach = function (fn, scope) {
      for (let i = 0, len = this.length; i < len; ++i) {
        fn.call(scope, this[i], i, this);
      }
    };
  }
  data.forEach(function (value, key) {
    // @ts-ignore
    object[key] = value;
  });
  return object;
};

export const formatPrivateKey = (privateKey: string): string => {
  const begin = '-----BEGIN RSA PRIVATE KEY-----\n';
  const end = '\n-----END RSA PRIVATE KEY-----';
  // Use a regular expression to insert newlines every 64 characters
  const formattedKey = privateKey.replace(/(.{64})/g, '$1\n');

  return `${begin}${formattedKey}${end}`;
};

export const createToken = (data: any): any => {
  const privateKey: any = process.env.REACT_APP_SECRET_KEY;
  const paramsOption: jwt.SignOptions = {
    algorithm: 'RS256',
    expiresIn: '2 days'
  };
  const formattedPrivateKey = formatPrivateKey(privateKey);
  const token: any = jwt.sign(data, formattedPrivateKey, paramsOption);
  return token;
};

export const encrypt = (data: string): any => {
  const ivKey: any = process.env.REACT_APP_ENCRYPTION_KEY;
  if (ivKey) {  
    const binaryEncryptionKey = CryptoJS.enc.Base64.parse(ivKey);
    const cipher = CryptoJS.AES.encrypt(data, binaryEncryptionKey, {
      mode: CryptoJS.mode.ECB,
      padding: CryptoJS.pad.Pkcs7
    });
    return cipher.toString();
  }
};

export const decrypt = (encryptedData: string): any => {
  const ivKey: any = process.env.REACT_APP_ENCRYPTION_KEY;
  if (ivKey) {
    const binaryEncryptionKey = CryptoJS.enc.Base64.parse(ivKey);
    const bytes = CryptoJS.AES.decrypt(encryptedData, binaryEncryptionKey, {
      mode: CryptoJS.mode.ECB,
      padding: CryptoJS.pad.Pkcs7
    });
    return bytes.toString(CryptoJS.enc.Utf8);
  }
};

export const getCookie = (cname: string): any => {
  let name = cname + "=";
  let ca = document.cookie.split(';');
  for(let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) == ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(name) == 0) {
      return c.substring(name.length, c.length);
    }
  }
  return "";
}

export const getEncryptedItem = (item: string) => {
  if (item) {
    const encodedKey = btoa(item);
    const itemValue = localStorage.getItem(encodedKey);
    if (itemValue) {
      return atob(itemValue);
    }
  }
  return null;
};

export const setEncryptedItem = (item: string, itemValue: string) => {
  if (item && itemValue) {
    const encodedKey = btoa(item);
    const encodedValue = btoa(itemValue);
    localStorage.setItem(encodedKey, encodedValue);
    return true;
  }
  return false;
};

export const removeEncryptedItem = (item: string) => {
  if (item) {
    const encodedKey = btoa(item);
    localStorage.removeItem(encodedKey);
    return true;
  }
  return false;
};

export const formatDateByLocale = (date: string | Date, locale: string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  // Format the date based on the locale
  return new Intl.DateTimeFormat(locale === 'en' ? 'en' : 'de-CH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(dateObj);
};
export const getUserDetails = (): any => {
  const expireCookie = getCookie(process.env.REACT_APP_EXPIRE_COOKIE || "");
  if (!expireCookie) {
    throw new Error("Expire cookie not found or invalid.");
  }
  
  const userExpireData = decodeURIComponent(expireCookie);
  
  try {
    const userDetails = JSON.parse(decrypt(userExpireData));
    return userDetails;
  } catch (error) {
    console.error("Error parsing user details: ", error);
    throw new Error("Unable to retrieve user details.");
  }
};

export const formatCurrency = (value: number, currency: string): string => {
  try {
    // Handle CHF (Swiss Franc) separately with custom formatting
    if (currency === "CHF") {
      // Format using the Swiss format for CHF (comma separator)
      const formattedValue = new Intl.NumberFormat("de-CH", {
        style: "currency",
        currency,
        minimumFractionDigits: 2,
      }).format(value);

      // Add space after the currency symbol
      return formattedValue.replace(/(\D)(\d)/, '$1 $2'); // Add a space after the currency symbol
    }

    // Format the currency for other currencies (using US format as fallback)
    const formattedValue = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
    }).format(value);

    // Add space between the currency symbol and the value
    return formattedValue.replace(/(\D)(\d)/, '$1 $2'); // Add a space after the currency symbol
  } catch (error) {
    console.error("Error formatting currency:", error);
    return value.toFixed(2); // Fallback formatting
  }
};

export const getDomainName = () => {
  const hostname = window.location.hostname; 
  const parts = hostname.split(".");

  if (parts.length > 2) {
    return parts.slice(1).join("."); 
  } else {
    return hostname;
  }
};

export const clearCookieStorage = ( hotReload = false) => {

  (async () => {
    let cookies = document.cookie.split("; ");
    for (let c = 0; c < cookies.length; c++) {
      let d = window.location.hostname.split(".");
      while (d.length > 0) {
        let cookieBase = encodeURIComponent(cookies[c].split(";")[0].split("=")[0]) + '=; expires=Thu, 01-Jan-1970 00:00:01 GMT; domain=' + d.join('.') + ' ;path=';
        let p = window.location.pathname.split('/');
        document.cookie = cookieBase + '/';
        while (p.length > 0) {
          document.cookie = cookieBase + p.join('/');
          p.pop();
        }
        d.shift();
      }
    }
    const host = window.location.host;
    const parts = host.split(".");
    parts.shift();

    let redirectUrl:string = localStorage.getItem('redirectUrl') || '';
    let encryptedData = encrypt(window.location.href);
    
    let result = redirectUrl.replace(/"/g, '');
    localStorage.clear();
    window.location.assign(result + 'auth/logout/' + encodeURIComponent(encryptedData));
    // await redirectToLogin(encryptionKey);
    
  })();
  if (hotReload)
    window.location.reload();
}